import { jsx as _jsx } from "react/jsx-runtime";
import { EM_DASH } from 'libs.constants';
import { Badge } from 'libs.nucleus';
import { getApiInstance } from './api.helpers';
import { PARTICIPANT_NUMBER_PREFIX } from './constants.helpers';
import { normalizeDate } from './date.helpers';
import { ParticipantStatuses } from '../constants/participants.constants';
const DUPLICATION_REASON_BY_ERROR_CODE = {
    'axon.validation.participantIDAlreadyExists': 'error.participant.id_exists',
};
const getDuplicateParticipantField = (fault) => {
    if (!fault) {
        return undefined;
    }
    if (fault.errCode === 'cortex.conflict.duplicateKey' && ['c_email', 'c_number'].includes(fault.path ?? '')) {
        return fault.path;
    }
    for (const nestedFault of fault.faults ?? []) {
        const field = getDuplicateParticipantField(nestedFault);
        if (field) {
            return field;
        }
    }
    return undefined;
};
export const getParticipantNumber = (rowData) => {
    if (rowData.c_number) {
        return rowData.c_number;
    }
    const { _id } = rowData;
    if (!_id) {
        return EM_DASH;
    }
    return `${PARTICIPANT_NUMBER_PREFIX}-${_id.substring(_id.length - 6, _id.length)}`;
};
export const getParticipantStatus = (status) => {
    if (status === ParticipantStatuses.CONSENTED) {
        return _jsx(Badge, { variant: 'success', label: status });
    }
    return _jsx(Badge, { label: status });
};
export const getParticipantDetails = async ({ environment, siteId, participantId, locale, }) => {
    try {
        const response = await getApiInstance(environment, locale).get(`/v2/routes/site_subjects/${siteId}`, {
            params: {
                expand: ['c_visit_schedule', 'c_caregiver_relationship.c_caregivers_info.c_public_user'],
                paths: ['c_set_dates'],
                where: JSON.stringify({ _id: participantId }),
            },
        });
        return response?.data?.data[0] || {};
    }
    catch {
        return {};
    }
};
export const isParticipantAuthenticated = (participant) => !!participant?.c_account;
export const isParticipantACaregiver = (participant) => participant?.c_type === 'caregiver';
export const participantHasLinkedCaregivers = (participant) => (participant?.c_caregiver_relationship?.c_caregivers_info?.length ?? 0) > 0;
export const isParticipantDeactivated = (participant) => participant?.c_status?.toLowerCase() === 'deactivated';
export const isParticipantInactive = (participant) => participant?.c_status?.toLowerCase() === 'inactive';
/**
 * Formats the participant's next visit window for display.
 *
 * The `c_next_visit_*` fields are plain dates (`YYYY-MM-DD`), so callers pass `formatPlainDate`
 * from `useDateTimeFormat()` to render them locale-, timezone-, and pseudo-localization-aware.
 * The range separator is localized via `translate` rather than a hardcoded string.
 */
export const getParticipantNextVisitWindow = (participant, formatPlainDate, translate) => {
    if (participant.c_next_visit_window_start && participant.c_next_visit_window_end) {
        return translate('label.participants_table.visit_window_range', {
            start: formatPlainDate(participant.c_next_visit_window_start),
            end: formatPlainDate(participant.c_next_visit_window_end),
        });
    }
    if (participant.c_next_visit_date) {
        return formatPlainDate(participant.c_next_visit_date);
    }
    return EM_DASH;
};
export const formatVisitWindow = (param) => {
    if (!param.windowStartDate || !param.windowEndDate) {
        return EM_DASH;
    }
    const startDate = normalizeDate(param.windowStartDate);
    const endDate = normalizeDate(param.windowEndDate);
    const startMonthName = new Intl.DateTimeFormat(param.currentLocale, { month: 'long' }).format(startDate);
    const endMonthName = new Intl.DateTimeFormat(param.currentLocale, { month: 'long' }).format(endDate);
    const year = startDate.getFullYear();
    // Check if the months are the same
    let periodStr;
    if (startMonthName === endMonthName) {
        const period = startDate.getDate() === endDate.getDate() ? startDate.getDate() : `${startDate.getDate()}-${endDate.getDate()}`;
        periodStr = `${startMonthName} ${period}, ${year}`;
    }
    else {
        periodStr = `${startMonthName} ${startDate.getDate()} - ${endMonthName} ${endDate.getDate()}, ${year}`;
    }
    return periodStr;
};
export const getParticipantDuplicateIndexErrorMessage = (error, translate) => {
    const errorData = error?.response?.data;
    const duplicateParticipantField = getDuplicateParticipantField(errorData);
    switch (duplicateParticipantField) {
        case 'c_number':
            return translate('error.participant.id_exists');
        case 'c_email':
            return translate('error.participant.email_exists');
    }
    if (errorData?.errCode && Object.keys(DUPLICATION_REASON_BY_ERROR_CODE).includes(errorData.errCode)) {
        return translate(DUPLICATION_REASON_BY_ERROR_CODE[errorData?.errCode]);
    }
};
export const shouldShowTelevistButton = (participant, org) => {
    const hasUserAcceptedInvite = participant?.c_invite === 'accepted';
    return !!org.c_televisit_enabled && hasUserAcceptedInvite;
};
